import React, { memo, useEffect, useMemo, useState } from 'react';
import { AIModel } from '@/types';
import { useAppStore } from '@/store';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuSub,
  DropdownMenuSubContent,
  DropdownMenuSubTrigger,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Bot, Check, ChevronDown, Cloud, Laptop, Server, Sparkles } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useIsMobile } from '@/hooks/use-mobile';
import { useLocalLLM } from '@/contexts/LocalLLMContext';
import { getSovereignDemoModels } from '@/lib/demoModels';

interface ModelSelectorProps {
  models: AIModel[];
  selectedModelId: string;
  onModelChange: (modelId: string) => void;
}

const ModelSelector: React.FC<ModelSelectorProps> = memo(({ models: availableModelsProp, selectedModelId, onModelChange }) => {
  const { user } = useAppStore();
  const isMobile = useIsMobile();
  const { isAvailable: localReady } = useLocalLLM();
  const [providerOrderKeys, setProviderOrderKeys] = useState<string[]>(['maplept', 'ollama_local', 'ollama_remote', 'local']);

  useEffect(() => {
    const saved = localStorage.getItem('modelSettings');
    if (!saved) return;

    const settings = JSON.parse(saved);
    if (Array.isArray(settings.providerOrder) && settings.providerOrder.length > 0) {
      setProviderOrderKeys(settings.providerOrder);
    }
  }, []);

  const availableModels = useMemo(() => {
    let base = availableModelsProp ? availableModelsProp.filter((model) => model.is_active) : [];

    if (localReady && !base.some((model) => model.id === 'maplept-free')) {
      base = [
        {
          id: 'maplept-free',
          name: 'MaplePT-Free',
          provider: 'local',
          description: 'Free compute on your device.',
          capabilities: ['text', 'tools'],
          tier: 'free',
          apiName: 'TinyLlama-1.1b-chat-v1.0',
          is_active: true,
          logo_url: '/images/models/maplept.svg',
        } as AIModel,
        ...base,
      ];
    }

    base = Array.from(new Map(base.map((model) => [model.id, model])).values());

    const tierFiltered = !user
      ? base.filter((model) => model.tier === 'free' || model.provider === 'local')
      : (() => {
          const tierLevels: Record<string, number> = { free: 0, basic: 1, standard: 2, premium: 3, enterprise: 3 };
          const userTierLevel = tierLevels[user.subscription] ?? tierLevels.free;

          return base.filter((model) => {
            if (model.provider === 'ollama' || model.provider === 'local') return true;
            const modelTierLevel = tierLevels[model.tier] ?? tierLevels.free;
            return userTierLevel >= 1 ? modelTierLevel <= userTierLevel : model.tier === 'free';
          });
        })();

    const sovereignDemoModels = getSovereignDemoModels(tierFiltered);
    return sovereignDemoModels.length > 0 ? sovereignDemoModels : tierFiltered;
  }, [availableModelsProp, localReady, user]);

  useEffect(() => {
    const selectedStillAvailable = availableModels.some((model) => model.id === selectedModelId);
    if (availableModels.length > 0 && (!selectedStillAvailable || !selectedModelId)) {
      onModelChange(availableModels[0].id);
    } else if (availableModels.length === 0 && selectedModelId) {
      onModelChange('');
    }
  }, [availableModels, onModelChange, selectedModelId]);

  const { groupedModels, providerOrder } = useMemo(() => {
    const groups: Record<string, AIModel[]> = {};

    const getProviderIcon = (providerKey: string, name: string, defaultIcon: React.ReactNode) => {
      const modelForProvider = availableModels.find((model) => {
        if (providerKey === 'ollama_remote') {
          return model.provider === 'ollama' && model.source === 'remote';
        }
        if (providerKey === 'ollama_local') {
          return model.provider === 'ollama' && model.source === 'local';
        }
        if (providerKey === 'local') {
          return model.provider === 'local';
        }
        return model.provider === providerKey;
      });

      return (
        <Avatar className="w-5 h-5 bg-muted">
          <AvatarImage src={modelForProvider?.logo_url} alt={name} />
          <AvatarFallback>{defaultIcon}</AvatarFallback>
        </Avatar>
      );
    };

    const baseOrder: Record<string, { name: string; type: 'cloud' | 'local'; icon: React.ReactNode }> = {
      maplept: { name: 'MaplePT', type: 'cloud', icon: getProviderIcon('maplept', 'MaplePT', <AvatarImage src="/images/models/maplept.svg" />) },
      ollama_remote: { name: 'Ollama', type: 'cloud', icon: getProviderIcon('ollama_remote', 'Ollama', <Cloud className="h-5 w-5 text-blue-600" />) },
      ollama_local: { name: 'Local Ollama', type: 'local', icon: getProviderIcon('ollama_local', 'Local Ollama', <Laptop className="h-5 w-5 text-green-600" />) },
      local: { name: 'Local', type: 'local', icon: getProviderIcon('local', 'Local', <Laptop className="h-5 w-5 text-green-600" />) },
    };

    availableModels.forEach((model) => {
      let key: string = model.provider;
      if (model.provider === 'ollama') {
        key = `ollama_${model.source}`;
      }

      if (model.id === 'maplept-free' || (model.provider === 'local' && model.name.toLowerCase().includes('maplept'))) {
        key = 'maplept';
      }
      if (!groups[key]) {
        groups[key] = [];
      }
      groups[key].push(model);
    });

    Object.values(groups).forEach((group) => group.sort((a, b) => a.name.localeCompare(b.name)));

    const filteredProviderOrder = providerOrderKeys
      .map((key) => ({ key, ...baseOrder[key] }))
      .filter((item) => item.name && groups[item.key]?.length > 0);

    return { groupedModels: groups, providerOrder: filteredProviderOrder };
  }, [availableModels, providerOrderKeys]);

  const selectedModelData = useMemo(
    () => availableModels.find((model) => model.id === selectedModelId),
    [availableModels, selectedModelId]
  );

  const renderProviderIcon = (model: AIModel) => {
    if (model.id === 'maplept-free') {
      return <AvatarImage src="/images/models/maplept.svg" alt="MaplePT" />;
    }
    if (model.logo_url) {
      return <AvatarImage src={model.logo_url} alt={model.name} />;
    }
    if (model.provider === 'ollama' && model.source === 'remote') {
      return <Cloud className="h-4 w-4 text-blue-600" />;
    }
    switch (model.provider) {
      case 'maplept':
        return <AvatarImage src="/images/models/maplept.svg" alt="MaplePT" />;
      case 'ollama':
        return model.source === 'local'
          ? <Laptop className="h-4 w-4 text-green-600" />
          : <Cloud className="h-4 w-4 text-blue-600" />;
      case 'local':
        return <Laptop className="h-4 w-4 text-green-600" />;
      case 'custom':
        return <Sparkles className="h-4 w-4 text-muted-foreground" />;
      default:
        return <Bot className="h-4 w-4 text-muted-foreground" />;
    }
  };

  const renderSelectedModelIcon = (model: AIModel) => {
    if (model.provider === 'ollama' && model.source === 'remote') {
      return <Cloud className="h-5 w-5 text-blue-600" />;
    }
    if (model.provider === 'ollama' && model.source === 'local') {
      return <Laptop className="h-5 w-5 text-green-600" />;
    }
    return <Avatar className="w-5 h-5 bg-muted">{renderProviderIcon(model)}</Avatar>;
  };

  const renderSubMenu = (providerInfo: typeof providerOrder[number]) => {
    const models = groupedModels[providerInfo.key];
    if (!models?.length) return null;

    return (
      <DropdownMenuSub key={providerInfo.key}>
        <DropdownMenuSubTrigger>
          <div className="flex items-center gap-2 w-full">
            <div className="flex items-center justify-center w-5 h-5">{providerInfo.icon}</div>
            <span>{providerInfo.name}</span>
            <span className="ml-auto text-xs text-muted-foreground">{models.length}</span>
          </div>
        </DropdownMenuSubTrigger>
        <DropdownMenuSubContent>
          {models.map((model) => (
            <DropdownMenuItem key={model.id} onSelect={() => onModelChange(model.id)}>
              <div className="flex items-center gap-2">
                <Avatar className="w-5 h-5 bg-muted">{renderProviderIcon(model)}</Avatar>
                <span>{model.name}</span>
              </div>
              {selectedModelId === model.id && <Check className="ml-auto h-4 w-4" />}
            </DropdownMenuItem>
          ))}
        </DropdownMenuSubContent>
      </DropdownMenuSub>
    );
  };

  const cloudMenus = providerOrder.filter((provider) => provider.type === 'cloud').map(renderSubMenu).filter(Boolean);
  const localMenus = providerOrder.filter((provider) => provider.type === 'local').map(renderSubMenu).filter(Boolean);

  return (
    <div className="flex items-center gap-2">
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button
            variant="outline"
            className={isMobile ? 'w-[150px] justify-between font-normal' : 'w-[320px] justify-between font-normal'}
            disabled={availableModels.length === 0}
          >
            {selectedModelData ? (
              <div className="flex items-center gap-2 truncate">
                {renderSelectedModelIcon(selectedModelData)}
                <span className="truncate">{selectedModelData.name}</span>
              </div>
            ) : (
              <span className="text-muted-foreground">Select a model</span>
            )}
            <ChevronDown className="h-4 w-4 opacity-50" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent className={isMobile ? 'w-[170px]' : 'w-[320px]'} align="start">
          {availableModels.length > 0 ? (
            <>
              {cloudMenus.length > 0 && (
                <DropdownMenuGroup>
                  <DropdownMenuLabel className="text-muted-foreground">Cloud Models</DropdownMenuLabel>
                  {cloudMenus}
                </DropdownMenuGroup>
              )}
              {cloudMenus.length > 0 && localMenus.length > 0 && <DropdownMenuSeparator />}
              {localMenus.length > 0 && (
                <DropdownMenuGroup>
                  <DropdownMenuLabel className="text-muted-foreground">Local Models</DropdownMenuLabel>
                  {localMenus}
                </DropdownMenuGroup>
              )}
            </>
          ) : (
            <div className="px-2 py-1.5 text-sm text-muted-foreground">No models available.</div>
          )}
        </DropdownMenuContent>
      </DropdownMenu>
      {selectedModelData && (
        <div className="text-xs text-muted-foreground truncate" title={selectedModelData.description}>
          {selectedModelData.description}
        </div>
      )}
    </div>
  );
});

export default ModelSelector;
